import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppConfig } from '../core/config/app.config';
import { Observable } from 'rxjs';
import { AssignModel } from './assign.model';
import { IAssignComment } from './assign-comments/assign-comment.interface';
import { AssessmentLinkModel } from './assessment-link.model';
import { ICommentsState } from '../shared/models/comments-state.interface';

@Injectable({
  providedIn: 'root',
})
export class AssignService {
  private http = inject(HttpClient);
  private config = inject(AppConfig);

  getAssignsByProject(projectId: number): Observable<AssignModel[]> {
    return this.http.get<AssignModel[]>(this.config.buildUri('assign') + '/AssignedPerProject?projectId=' + projectId);
  }

  getAssign(id: number): Observable<AssignModel> {
    return this.http.get<AssignModel>(this.config.buildUri('assign') + '/SingleAssignment?assignmentId=' + id);
  }

  deleteAssign(id: number, deleteGroup: boolean): Observable<unknown> {
    let url = this.config.buildUri('assign') + '/AssignedForm?id=' + id;
    if (deleteGroup) {
      url = this.config.buildUri('assign') + '/AssignedForm?id=' + id + '&deleteGroup=' + deleteGroup;
    }

    return this.http.delete<unknown>(url);
  }

  assignToUsers(data): Observable<unknown> {
    delete data.id;
    return this.http.post<unknown>(this.config.buildUri('assign') + '/UserAssignment', data);
  }

  assignToGroup(data): Observable<unknown> {
    delete data.id;
    return this.http.post<unknown>(this.config.buildUri('assign') + '/SecurityGroupAssignment', data);
  }

  assignToRole(data): Observable<unknown> {
    return this.http.post<unknown>(this.config.buildUri('assign') + '/AssignFormToRole', data);
  }

  assignToEmails(data): Observable<unknown> {
    return this.http.post<unknown>(this.config.buildUri('assign') + '/AddFormToEmailOfUser', data);
  }

  createAssignment(assignment: AssignModel): Observable<AssignModel> {
    return this.http.post<AssignModel>(this.config.buildUri('assign') + '/CreateAssignment?isAmend=false', assignment);
  }

  createAssignments(assignments: AssignModel[]): Observable<AssignModel[]> {
    return this.http.post<AssignModel[]>(this.config.buildUri('assign') + '/CreateAssignments?isAmend=false', assignments);
  }

  getCommentsByAssignId(assignmentId: number): Observable<IAssignComment[]> {
    return this.http.get<IAssignComment[]>(
      this.config.buildUri('assessmentComments') + '/AssessmentComments' + '?assignmentId=' + assignmentId,
    );
  }

  updateComment(data: Partial<IAssignComment>): Observable<IAssignComment> {
    return this.http.put<IAssignComment>(this.config.buildUri('assessmentComments') + '/AssessmentComments', data);
  }

  createComment(data: Partial<IAssignComment>): Observable<IAssignComment> {
    return this.http.post<IAssignComment>(this.config.buildUri('assessmentComments') + '/CreateAssessmentComments', data);
  }

  deleteComment(commentId: number): Observable<unknown> {
    return this.http.delete(
      this.config.buildUri('assessmentComments') + '/AssessmentComments' + '?assessmentCommentsId=' + commentId,
    );
  }

  resolveComment(commentId: number): Observable<IAssignComment> {
    return this.http.post<IAssignComment>(
      this.config.buildUri('assessmentComments') + '/MarkCommentAsResolved' + '?assessmentCommentId=' + commentId,
      {},
    );
  }

  getCommentsCounter(projectId: number): Observable<ICommentsState[]> {
    return this.http.get<ICommentsState[]>(
      this.config.buildUri('assessmentComments') + '/AssessmentCommentCount' + '?projectId=' + projectId,
    );
  }

  checkAssignmentsCommentsStatus(assignmentsIds: number[]): Observable<{ canChangeStatuses: boolean }> {
    return this.http.post<{ canChangeStatuses: boolean }>(this.config.buildUri('assessmentComments') + '/CheckAllResolved', assignmentsIds);
  }

  updateAssignments(assignments: AssignModel[]): Observable<AssignModel[]> {
    assignments = assignments.map(assessment => ({
      ...assessment,
      formId: assessment.form?.id ?? assessment.formId,
      formVersionId: assessment.form?.id ?? assessment.formId,
    }));

    return this.http.put<AssignModel[]>(this.config.buildUri('assign') + '/UserAssignments', assignments);
  }

  getAssessmentLinks(assessmentId: number): Observable<AssessmentLinkModel> {
    return this.http.get<AssessmentLinkModel>(this.config.buildUri('assessmentLinks') + '/AssessmentLinks?assignmentId=' + assessmentId);
  }

  createAssessmentLink(data: AssessmentLinkModel): Observable<AssessmentLinkModel> {
    return this.http.post<AssessmentLinkModel>(this.config.buildUri('assessmentLinks') + '/CreateAssessmentLinks', data);
  }

  updateAssessmentLink(data: AssessmentLinkModel): Observable<AssessmentLinkModel> {
    return this.http.put<AssessmentLinkModel>(this.config.buildUri('assessmentLinks') + '/AssessmentLinks', data);
  }

  /**
   * Hard deletes all linked assessments from the given AssessmentLinkModel.
   * Permanently removes all LinkedAssessmentItem entries from the linkedAssessments list.
   */
  softDeleteAllLinkedAssessments(assessmentId: number): Observable<unknown> {
    return this.http.post(
      this.config.buildUri('assessmentLinks') + '/SoftDeleteAssessmentLinks?assignmentId=' + assessmentId,
      null,
    );
  }

  /**
   * Soft deletes all linked assessments from the given AssessmentLinkModel.
   * Marks all LinkedAssessmentItem entries in the linkedAssessments list as isDeleted = true,
   * without physically removing them.
   */
  hardDeleteAllLinkedAssessments(assessmentId: number): Observable<unknown> {
    return this.http.delete(
      this.config.buildUri('assessmentLinks') + '/HardDeleteAssessmentLinksByAssignmentId?assignmentId=' + assessmentId,
    );
  }

  /**
   * Soft deletes a linked assessment with the specified assignmentId
   * from all available AssessmentLinkModel instances.
   * Marks the LinkedAssessmentItem as isDeleted = true instead of removing it.
   */
  softDeleteLinkedAssessmentByAssignmentId(assessmentId: number): Observable<unknown> {
    return this.http.post(
      this.config.buildUri('assessmentLinks') +
        '/SoftDeleteAssessmentLinksByLinkedAssessmentId?linkedAssignmentId=' +
        assessmentId,
      null,
    );
  }

  /**
   * Hard deletes a linked assessment with the specified assignmentId
   * from all available AssessmentLinkModel instances.
   * Permanently removes the LinkedAssessmentItem from their linkedAssessments lists.
   */
  hardDeleteLinkedAssessmentByAssignmentId(assessmentId: number): Observable<unknown> {
    return this.http.delete(
      this.config.buildUri('assessmentLinks') +
        '/HardDeleteAssessmentLinksByLinkedAssessmentId?linkedAssignmentId=' +
        assessmentId,
    );
  }
}
